import React, { useEffect, useState } from 'react';

import { API } from 'constants';
import reportClient from '../../../../services/report-api';
import operateClient from '../../../../services/operate-api';

import {
    checkIsFormDataValid,
    formatInputsToFormData,
    replaceFilesWithCDNUrls,
    getStartNewJobFormDataInitialState,
    handleClientStartNewJobRequestErrors,
} from '../../../../helpers/startNewJobFormUtils';
import { capitalizeStr } from '../../../../helpers/formatOrganizationDataForTable';
import { mutateListDataAfterUpdatingItem } from '../../../../helpers/swrMutateUtils';

import Panel from '../../../../components/Panel';
import Loading from '../../../../components/Loading';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import NewJobForm from '../../../../components/NewJobForm/NewJobForm';
import PlayCircleFillIcon from '../../../../design-system/Icons/PlayCircleFillIcon';
import { CloseLineIcon } from '../../../../design-system/Icons';
import { Button, ButtonIcon } from '../../../../design-system';

const ROUTE = {
    job: API.ROUTES.report.job,
    thread: API.ROUTES.operate.thread,
    execution: API.ROUTES.operate.execution,
};

const CLIENT = {
    job: reportClient,
    thread: operateClient,
    execution: operateClient,
};

const RestartPanel = ({
    target = 'job',
    id,
    mutate,
    setUpdatedItem,
    requestKeysToMutate = [],
    onClose,
}) => {
    const [formData, setFormData] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const client = CLIENT[target];
    const requestEndpoint = ROUTE[target];

    useEffect(() => {
        const fetchRestartFormData = async () => {
            try {
                const { data } = await client.get(`${requestEndpoint}${id}/restart/`);
                setFormData(() =>
                    getStartNewJobFormDataInitialState({
                        inputs: data.inputs,
                        allowDocsMapping: true,
                        replaceOnlyFilledTags: true,
                    })
                );
            } catch (e) {
                onClose();
            }
        };

        fetchRestartFormData();
    }, []);

    const handleRestart = async () => {
        const isFormDataValid = checkIsFormDataValid(formData, setFormData);
        if (!isFormDataValid) {
            return;
        }

        try {
            setIsLoading(true);

            const formDataWithCDNUrls = await replaceFilesWithCDNUrls(formData);
            const inputs = formatInputsToFormData(formDataWithCDNUrls);

            const { data } = await client.post(`${requestEndpoint}${id}/restart/`, {
                inputs,
            });

            if (mutate) {
                await mutate();
            }
            const updatedPart = {
                status: data.status,
                updated_at: data.updated_at,
            };
            if (!!requestKeysToMutate.length) {
                mutateListDataAfterUpdatingItem({ requestKeysToMutate, id, updatedPart });
            }
            if (setUpdatedItem) {
                setUpdatedItem((item) => ({ ...item, ...updatedPart }));
            }

            setIsLoading(false);
            onClose();
        } catch (error) {
            setIsLoading(false);
            handleClientStartNewJobRequestErrors({ error, setErrorAlert, formData, setFormData });
        }
    };

    return (
        <Panel onClose={onClose} resetPadding showControls={false}>
            <div className="flex flex-col gap-5 sm:gap-7 px-5 py-5 sm:px-8 lg:px-[40px] lg:py-8">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="font-heading-bold text-heading-bold-m text-black mb-2">
                            Restart {capitalizeStr(target)}
                        </p>
                        <p className="font-body text-body-regular-s text-neutral-400">
                            Edit the current {target} inputs and click Restart{' '}
                            {capitalizeStr(target)}
                        </p>
                    </div>
                    <ButtonIcon type="link" size="sm" icon={CloseLineIcon} onClick={onClose} />
                </div>

                {!!formData && (
                    <>
                        <NewJobForm
                            formData={formData}
                            setFormData={setFormData}
                            containerGap={28}
                        />
                        <div className="py-2 sm:py-4">
                            <div className="pt-4 border-t-1 border-neutral-200 flex justify-end">
                                <Button
                                    type="secondary"
                                    size="sm"
                                    text={`Restart ${capitalizeStr(target)}`}
                                    state={isLoading ? 'loading' : 'default'}
                                    trailingIcon={target !== 'execution' && PlayCircleFillIcon}
                                    onClick={handleRestart}
                                />
                            </div>
                        </div>
                    </>
                )}

                {!formData && (
                    <div className="flex justify-center py-2">
                        <Loading withText={false} />
                    </div>
                )}
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Panel>
    );
};

export default RestartPanel;
